import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { UserAuthService } from 'src/app/@services/user-auth.service';
import { AuthStateService } from 'src/app/@stores/auth-state.service';
import { AUTH_ROUTES } from '../constants/auth-constants';
import { AuthSessionService } from '../services/auth-session';
import {
  extractEventNameFromReturnUrl,
  normalizeReturnUrlForRouter,
} from 'src/app/@utils/reports-navigation';

export const loginRedirectGuard: CanActivateFn = (
  route,
  _state
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const sessionService = inject(AuthSessionService);
  const userAuthService = inject(UserAuthService);
  const authStateService = inject(AuthStateService);

  const returnUrl = route.queryParams['returnUrl'] ?? '';
  const eventName = extractEventNameFromReturnUrl(returnUrl) ?? '';
  const hasEventContext = eventName !== '';

  // No event context — we don't know which event to authenticate for
  if (!hasEventContext) {
    return of(router.createUrlTree([AUTH_ROUTES.HOME]));
  }

  // Shared username/password events do not use Cognito. Only leave /login when
  // AUTH_STATE is already valid for this event.
  const authHint = route.queryParams['auth'];
  const passwordHint =
    authHint === 'password' || authHint === 'USERNAME/PASSWORD';
  if (passwordHint || userAuthService.isUsernamePasswordLogin()) {
    if (eventName && userAuthService.checkExistingAuth(eventName)) {
      return of(router.createUrlTree([returnUrl]));
    }
    return of(true);
  }

  // If config is not loaded yet, still allow the login page (guard on event
  // routes loads config before redirecting here).
  if (authStateService.$status() === 'authenticated' && eventName) {
    if (userAuthService.checkExistingAuth(eventName)) {
      return of(router.createUrlTree([returnUrl]));
    }
  }

  return sessionService.checkSession$().pipe(
    take(1),
    map((session) => {
      if (!session.isAuthenticated) {
        return true;
      }

      if (
        returnUrl &&
        returnUrl.startsWith('/') &&
        !returnUrl.includes('//') &&
        !returnUrl.includes(AUTH_ROUTES.LOGIN)
      ) {
        return router.parseUrl(normalizeReturnUrlForRouter(returnUrl));
      }

      return router.createUrlTree([AUTH_ROUTES.HOME]);
    }),
    catchError(() => of(true))
  );
};
